import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Legend, Tooltip } from "recharts";
import axios from "axios";
import Loader from "../../../loader/Loader";
import ChartWrapper from "./ChartWrapper";
import { domain } from "../../../../security";
import { IoChevronDown } from "react-icons/io5";

const COLORS = [
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#0088FE",
  "#FF66C4",
  "#8884d8",
];

const LocationPieChart = ({ sourceType, selectedLocation }) => {
  const [locationData, setLocationData] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    { value: 1, label: "Jan" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "May" },
    { value: 6, label: "Jun" },
    { value: 7, label: "Jul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append("sourceType", sourceType);
        params.append("locationId", selectedLocation);
        params.append("month", selectedMonth);
        params.append("year", selectedYear);

        const response = await axios.get(
          `${domain}/api/Dashboard/sales-by-location?${params}`,
        );
        setLocationData(response.data);
      } catch (error) {
        console.error("Error fetching chart data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sourceType, selectedLocation, selectedMonth, selectedYear]);

  const renderLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
    const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);

    if (percent === 0) return null;

    return (
      <text
        x={x}
        y={y}
        fill="white"
        fontSize={12}
        fontWeight="bold"
        textAnchor="middle"
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const filterControls = (
    <>
      <div className="relative">
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          className="appearance-none bg-slate-50 text-slate-700 border border-slate-200 rounded-xl px-3 py-2 pr-8 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500/20 cursor-pointer hover:bg-slate-100 transition-colors"
        >
          {months.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        <IoChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xs" />
      </div>
      <div className="relative">
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="appearance-none bg-slate-50 text-slate-700 border border-slate-200 rounded-xl px-3 py-2 pr-8 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500/20 cursor-pointer hover:bg-slate-100 transition-colors"
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <IoChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xs" />
      </div>
    </>
  );

  if (loading) return <Loader />;

  return (
    <ChartWrapper title="Sales by Location" controls={filterControls}>
      {locationData.length === 0 ? (
        <div className="flex items-center justify-center h-full text-slate-400 text-sm">
          No data available for this period.
        </div>
      ) : (
        <PieChart>
          <Pie
            data={locationData}
            cx="50%"
            cy="45%"
            labelLine={false}
            label={renderLabel}
            innerRadius={40}
            outerRadius="80%"
            fill="#8884d8"
            dataKey="value"
            paddingAngle={2}
          >
            {locationData.map((_, i) => (
              <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => [`${value.toLocaleString()}`, "Qty"]}
            contentStyle={{
              backgroundColor: "#222",
              borderRadius: "8px",
              border: "none",
              color: "#fff",
            }}
            itemStyle={{ color: "#fff" }}
          />
          <Legend
            layout="horizontal"
            verticalAlign="bottom"
            align="center"
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ fontSize: "12px", paddingTop: "10px" }}
          />
        </PieChart>
      )}
    </ChartWrapper>
  );
};

export default LocationPieChart;
